import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
    }
    return Promise.reject(err);
  }
);

// Public endpoints
export const fetchHome = () => api.get('/home');
export const fetchSports = () => api.get('/sports');
export const fetchSportDetail = (slug, params = {}) => api.get(`/sports/${slug}`, { params });
export const fetchLeagues = (sport) => api.get('/leagues', { params: { sport } });
export const fetchLeagueDetail = (slug) => api.get(`/leagues/${slug}`);
export const fetchMatches = (params) => api.get('/matches', { params });
export const fetchMatchDetail = (slug) => api.get(`/matches/${slug}`);
export const fetchMatchOdds = (slug, market) => api.get(`/matches/${slug}/odds`, { params: { market } });
export const fetchMatchHistory = (slug, market) => api.get(`/matches/${slug}/history`, { params: { market } });
export const fetchMatchStatistics = (slug) => api.get(`/matches/${slug}/statistics`);
export const fetchPredictionsBatch = (matchIds) => api.post('/matches/predictions-batch', matchIds);
export const fetchFavoriteMatches = (slugs) => api.get('/favorites/matches', { params: { slugs } });
export const fetchValueBets = (params) => api.get('/value-bets', { params });
export const fetchSurebets = (params) => api.get('/surebets', { params });
export const fetchOddsMovements = (params) => api.get('/odds-movements', { params });
export const fetchBookmakers = () => api.get('/bookmakers');
export const fetchBookmakerDetail = (slug) => api.get(`/bookmakers/${slug}`);
export const trackBookmakerClick = (id, name = '', matchSlug = '', source = '') =>
  api.post('/affiliate/click', { bookmaker_id: id, bookmaker_name: name, match_slug: matchSlug, source_page: source });
export const searchAPI = (q) => api.get('/search', { params: { q } });

// Auth endpoints
export const loginAPI = (username, password) => api.post('/auth/login', { username, password });
export const getMeAPI = () => api.get('/auth/me');

// Admin endpoints
export const fetchAdminDashboard = () => api.get('/admin/dashboard');
export const fetchAdminLogs = (params) => api.get('/admin/logs', { params });
export const fetchMappingQueue = (params) => api.get('/admin/mapping/review-queue', { params });
export const fetchMappingGrouped = (params) => api.get('/admin/mapping/grouped', { params });
export const approveMapping = (data) => api.post('/admin/mapping/approve', data);
export const rejectMapping = (data) => api.post('/admin/mapping/reject', data);
export const bulkMappingAction = (data) => api.post('/admin/mapping/bulk-action', data);
export const groupMappingAction = (data) => api.post('/admin/mapping/group-action', data);
export const bulkGroupMappingAction = (data) => api.post('/admin/mapping/bulk-group-action', data);
export const fetchMappingCandidates = (entityType) => api.get(`/admin/mapping/candidates/${entityType}`);
export const fetchAdminBookmakers = () => api.get('/admin/bookmakers');
export const updateBookmaker = (id, data) => api.put(`/admin/bookmakers/${id}`, data);
export const fetchAffiliateSummary = () => api.get('/admin/affiliate/summary');
export const fetchJobsStatus = () => api.get('/admin/jobs/status');
export const manualRefresh = () => api.post('/admin/manual-refresh');
export const clearCache = (pattern) => api.post('/admin/cache/clear', null, { params: { pattern } });
export const fetchCacheStats = () => api.get('/admin/cache/stats');
export const fetchSportsManagement = () => api.get('/admin/sports-management');
export const fetchAdminUsers = () => api.get('/admin/users');

// P2: Alerts
export const fetchAlerts = (params) => api.get('/admin/alerts', { params });
export const markAlertRead = (id) => api.post(`/admin/alerts/read/${id}`);
export const markAllAlertsRead = () => api.post('/admin/alerts/read-all');
export const fetchAlertPreferences = () => api.get('/admin/alerts/preferences');
export const updateAlertPreference = (data) => api.post('/admin/alerts/preferences', data);
export const createAlert = (data) => api.post('/admin/alerts/create', data);
export const deleteAlert = (id) => api.delete(`/admin/alerts/${id}`);

// P3: Entity Management
export const fetchAdminEntSports = (params) => api.get('/admin/entities/sports', { params });
export const updateAdminSport = (id, data) => api.put(`/admin/entities/sports/${id}`, data);
export const fetchAdminEntLeagues = (params) => api.get('/admin/entities/leagues', { params });
export const updateAdminLeague = (id, data) => api.put(`/admin/entities/leagues/${id}`, data);
export const fetchAdminEntTeams = (params) => api.get('/admin/entities/teams', { params });
export const updateAdminTeam = (id, data) => api.put(`/admin/entities/teams/${id}`, data);
export const fetchAdminEntMarkets = (params) => api.get('/admin/entities/markets', { params });
export const updateAdminMarket = (id, data) => api.put(`/admin/entities/markets/${id}`, data);

// P4: Data Quality
export const fetchDataQuality = () => api.get('/admin/data-quality');

export default api;
